import { collection, onSnapshot, orderBy, query, where, limit } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import firebaseFirestore from '~/config/firebase/firestore';
import {
  COLECCION_EVENTOS,
  EventoReserva,
  momentoDe,
  reconstruirDesdeReserva,
} from '~/core/tracking/reservationEvents';

/**
 * Historial completo de UNA reserva, en tiempo real.
 *
 * Junta dos fuentes y las ordena por hora:
 *  · los eventos registrados desde que existe el rastreo, que son la verdad;
 *  · lo reconstruido a partir de la propia reserva, para las anteriores.
 *
 * Así se puede preguntar por cualquier reserva, también por las de hace meses,
 * sin haber migrado nada.
 */
export const useHistorialReserva = (reserva?: Record<string, any> | null) => {
  const [registrados, setRegistrados] = useState<EventoReserva[]>([]);
  const [cargando, setCargando] = useState(true);

  const reservationId = reserva?.id as string | undefined;

  useEffect(() => {
    if (!reservationId) {
      setRegistrados([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    // Sin `orderBy` a propósito: filtrar y ordenar a la vez obligaría a crear
    // un índice compuesto, y son pocos eventos por reserva. Se ordena abajo.
    const q = query(
      collection(firebaseFirestore, COLECCION_EVENTOS),
      where('reservationId', '==', reservationId),
      limit(200)
    );
    const cancelar = onSnapshot(
      q,
      (snap) => {
        setRegistrados(snap.docs.map((d) => ({ id: d.id, ...d.data() } as EventoReserva)));
        setCargando(false);
      },
      () => setCargando(false)
    );
    return () => cancelar();
  }, [reservationId]);

  const eventos = useMemo(() => {
    if (!reserva) return [];
    const reconstruidos = reconstruirDesdeReserva(reserva);
    // Si una acción ya está registrada de verdad, se descarta la reconstruida
    // equivalente para no contar dos veces lo mismo.
    const tiposRegistrados = new Set(registrados.map((e) => e.type));
    const utiles = reconstruidos.filter((e) => !tiposRegistrados.has(e.type));
    const todos = [...registrados, ...utiles];

    // Crear una reserva siempre es lo primero que le pasa. Si su fecha es
    // posterior a cualquier otro hecho, está pisada: al modificar una reserva
    // se sobrescribía `createdAt` con la fecha del momento. Se coloca delante
    // y se avisa, en lugar de dar por buena una fecha imposible.
    const creacion = todos.find((e) => e.type === 'created');
    const otros = todos.filter((e) => e !== creacion).map(momentoDe).filter(Boolean);
    let clave = (e: EventoReserva) => momentoDe(e);
    if (creacion && otros.length) {
      const primero = Math.min(...otros);
      if (momentoDe(creacion) > primero) {
        creacion.notaFecha = {
          corta: 'La fecha de creación se perdió',
          detalle:
            'Al modificar una reserva se sobrescribía su fecha de creación con la del ' +
            'momento, así que la guardada es posterior a hechos que ya habían ocurrido. ' +
            'Ya está corregido para las nuevas.',
        };
        clave = (e) => (e === creacion ? primero - 1 : momentoDe(e));
      }
    }
    return todos.sort((a, b) => clave(a) - clave(b));
  }, [registrados, reserva]);

  return { eventos, cargando };
};

export type FiltrosActividad = {
  tipo?: string;
  locationId?: string;
  desde?: Date | null;
  hasta?: Date | null;
  busqueda?: string;
};

/**
 * Actividad reciente de toda una empresa, en tiempo real.
 *
 * Solo filtra por empresa en la consulta —para no depender de índices
 * compuestos que habría que ir creando— y el resto se afina en memoria, que
 * con unos cientos de eventos es instantáneo y permite buscar por texto.
 */
export const useActividadEmpresa = (companyId?: string, tope = 300) => {
  const [eventos, setEventos] = useState<EventoReserva[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setEventos([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    const q = query(
      collection(firebaseFirestore, COLECCION_EVENTOS),
      where('companyId', '==', companyId),
      orderBy('at', 'desc'),
      limit(tope)
    );
    const cancelar = onSnapshot(
      q,
      (snap) => {
        setEventos(snap.docs.map((d) => ({ id: d.id, ...d.data() } as EventoReserva)));
        setCargando(false);
        setError(null);
      },
      (e) => {
        setError(e.message);
        setCargando(false);
      }
    );
    return () => cancelar();
  }, [companyId, tope]);

  return { eventos, cargando, error };
};

/** Aplica los filtros de la pantalla sobre los eventos ya cargados. */
export const filtrarEventos = (
  eventos: EventoReserva[],
  filtros: FiltrosActividad
): EventoReserva[] => {
  const texto = (filtros.busqueda ?? '').trim().toLowerCase();
  return eventos.filter((e) => {
    if (filtros.tipo && filtros.tipo !== 'todos' && e.type !== filtros.tipo) return false;
    if (filtros.locationId && e.locationId !== filtros.locationId) return false;
    if (filtros.desde && momentoDe(e) < filtros.desde.getTime()) return false;
    if (filtros.hasta && momentoDe(e) > filtros.hasta.getTime()) return false;
    if (texto) {
      const donde = [
        e.code,
        e.customer?.name,
        e.customer?.email,
        e.actor?.email,
        e.actor?.name,
        e.locationName,
        e.summary,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!donde.includes(texto)) return false;
    }
    return true;
  });
};
